/**
 * New Job "Destination" list from the ranked recommendations (Phase 28 / task 28.5,
 * DEC-331) — pure, unit-tested.
 *
 * GET /v1/disposal-recommendations returns EVERY usable place (active facilities and the
 * company's drop-off locations), already in the dispatcher's display mode: ranked with
 * dollars (FULL_COST), ranked without (PRIORITY_ONLY), or alphabetical with nothing about
 * cost (off / HIDDEN). Because the list is complete, it also ends the old silent cut-off at
 * the first 50 facilities alphabetically.
 *
 * A place on this customer's "never send here" list stays pickable (D-14 A: warn, never
 * block); its label says so, and the form asks for a reason before creating the job.
 */

import { formatDollarsFromCents, formatNumber } from '@lodex-pro/shared/number-format';

/** SWD destination A1 (0208): WELL is a catalog disposal well the company authorized. */
export type DestinationKind = 'FACILITY' | 'LOCATION' | 'WELL';

export interface RecommendedDestination {
  readonly kind: DestinationKind;
  readonly id: string;
  readonly name: string;
  readonly rank: number | null;
  readonly status: 'COMPLETE' | 'NO_RATE' | 'NO_LOCATION';
  readonly excluded: boolean;
  readonly excludedReason: string | null;
  readonly recommended: boolean;
  readonly milesHundredths?: number | null;
  readonly rateHundredthsCentPerBbl?: number | null;
  readonly totalCents?: number | null;
}

export interface DisposalRecommendationsResponse {
  readonly enabled: boolean;
  readonly display: 'FULL_COST' | 'PRIORITY_ONLY' | 'HIDDEN';
  readonly shown: boolean;
  readonly netBbl: string;
  readonly bblSource: 'CAPTURE' | 'TYPICAL_PICKUP' | 'TENANT_DEFAULT';
  readonly confidence: string | null;
  readonly options: readonly RecommendedDestination[];
}

export interface DestinationOption {
  /** `FACILITY:<id>` or `LOCATION:<id>` — the two id spaces never collide this way. */
  readonly value: string;
  readonly label: string;
}

export function destinationValue(kind: DestinationKind, id: string): string {
  return `${kind}:${id}`;
}

export function parseDestinationValue(
  value: string,
): { readonly kind: DestinationKind; readonly id: string } | null {
  const i = value.indexOf(':');
  if (i < 0) return null;
  const kind = value.slice(0, i);
  const id = value.slice(i + 1);
  if ((kind !== 'FACILITY' && kind !== 'LOCATION' && kind !== 'WELL') || id === '') return null;
  return { kind, id };
}

/** The create-job body fields for a destination value ({} for none). */
export function destinationPayload(value: string): {
  plannedDisposalFacilityId?: string;
  plannedDropoffHaulerLocationId?: string;
  plannedDropoffDisposalWellId?: string;
} {
  const d = parseDestinationValue(value);
  if (d === null) return {};
  if (d.kind === 'FACILITY') return { plannedDisposalFacilityId: d.id };
  if (d.kind === 'LOCATION') return { plannedDropoffHaulerLocationId: d.id };
  return { plannedDropoffDisposalWellId: d.id };
}

function costPart(o: RecommendedDestination): string | null {
  if (o.status === 'NO_LOCATION') return 'no map location';
  if (o.status === 'NO_RATE') return 'no price on file';
  if (o.totalCents === undefined || o.totalCents === null) return null;
  const miles =
    o.milesHundredths === undefined || o.milesHundredths === null
      ? ''
      : ` · ${formatNumber(o.milesHundredths / 100, { decimals: 1 })} mi`;
  return `${formatDollarsFromCents(o.totalCents)}${miles}`;
}

/** One picker label. Ranked lists number each place; excluded places say so first. */
export function destinationLabel(o: RecommendedDestination, shown: boolean): string {
  const parts: string[] = [];
  if (shown && o.rank !== null) parts.push(`${String(o.rank)}. ${o.name}`);
  else parts.push(o.name);
  if (shown) {
    const cost = costPart(o);
    if (cost !== null) parts.push(cost);
    if (o.recommended) parts.push('suggested');
  }
  if (o.excluded) parts.push(`Customer excluded: ${o.excludedReason ?? 'no reason given'}`);
  return parts.join(' — ');
}

/**
 * The picker's options. Before a pickup is chosen there is nothing to measure from, so the
 * list is plain names A→Z (still complete, still marking excluded places).
 */
export function buildDestinationOptions(
  res: DisposalRecommendationsResponse | undefined,
  pickupChosen: boolean,
): DestinationOption[] {
  if (res === undefined) return [];
  const shown = res.shown && pickupChosen;
  // SWD A3 — every kind is offered, the company's authorized disposal wells included: the
  // phone now shows a planned well and takes the driver there (#906 review MED1 hid it until then).
  const ordered = shown ? res.options : [...res.options].sort(byName);
  return ordered.map((o) => ({
    value: destinationValue(o.kind, o.id),
    label: destinationLabel(o, shown),
  }));
}

function byName(a: RecommendedDestination, b: RecommendedDestination): number {
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

/** The chosen place when it is on this customer's "never send here" list, else null. */
export function excludedChoice(
  res: DisposalRecommendationsResponse | undefined,
  value: string,
): RecommendedDestination | null {
  const d = parseDestinationValue(value);
  if (res === undefined || d === null) return null;
  return res.options.find((o) => o.kind === d.kind && o.id === d.id && o.excluded) ?? null;
}

/** The server's floor for a typed override reason (create.ts disposalOverrideReason). */
export const MIN_OVERRIDE_REASON = 3;

/** The query string for the pickup ('' when none is chosen yet). */
export function recommendationsQuery(pickupSel: string): string {
  if (pickupSel.startsWith('tb:'))
    return `?tankBatteryId=${encodeURIComponent(pickupSel.slice(3))}`;
  if (pickupSel.startsWith('wl:')) return `?pickupWellId=${encodeURIComponent(pickupSel.slice(3))}`;
  return '';
}

/** A one-line note under the picker saying what the costs assume (D-3: estimates). */
export function estimateNote(
  res: DisposalRecommendationsResponse | undefined,
  pickupChosen: boolean,
): string | null {
  if (res === undefined || !res.shown || !pickupChosen) return null;
  const basis =
    res.bblSource === 'TYPICAL_PICKUP'
      ? 'the average of recent loads from this pickup'
      : "the company's typical load";
  return `Ranked cheapest first, as estimates for ${res.netBbl} bbl (${basis}).`;
}
